import React, { lazy, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectToken, selectUser } from './store/reducer/authSlice';
import UserReSetPassword from './pages/auth/UserReSetPassword';
import DeliveryPartners from './pages/deliverypartner/DeliveryPartners';
import CustomPrintLabelPage from './pages/admin/CustomPrintLabelPage';
import Cancel from './pages/Cancel';
import OrderTracking from './pages/admin/OrderTracking';
import Packaging from './pages/Packaging';
import SubscriptionsList from './pages/profile/SubscriptionsList';
import SubscriptionsDetails from './pages/profile/SubscriptionsDetails';
import OrderValidationList from './pages/admin/OrderValidationList';
import ZellePaymentList from './pages/admin/ZellePaymentList';
import AdminSubscriptionDetailsPage from './pages/admin/AdminSubscriptionDetailsPage';

// ============================================
// Authentication Pages
// ============================================
const Signin = lazy(() => import('./pages/auth/Signin'));
const OtpVerification = lazy(() => import('./pages/auth/OtpVerification'));
const ForgetPassword = lazy(() => import('./pages/auth/ForgetPassword'));
const SignUp = lazy(() => import('./pages/auth/SignUp'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));

// ============================================
// Public Pages
// ============================================
const FaqPage = lazy(() => import('./pages/FaqPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const WhyHalal = lazy(() => import('./pages/WhyHalal'));
const HappyCustomersPage = lazy(() => import('./pages/HappyCustomersPage'));
const TermsConditionsPage = lazy(() => import('./pages/TermsConditionsPage'));
const HomePage = lazy(() => import('./pages/home/HomePage'));
const ProductsByCategoryPage = lazy(() => import('./pages/ProductsByCategoryPage'));
const BuyPage = lazy(() => import('./pages/BuyPage'));
const OthersPages = lazy(() => import('./pages/OthersPages'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const QurbaniPage = lazy(() => import('./pages/qurbani/Qurbani'));
const QurbaniOrderSummary = lazy(() => import('./pages/qurbani/QurbaniOrderSummary'));
const QurbaniCheckoutStepper = lazy(() => import('./pages/qurbani/QurbaniCheckoutStepper'));
const QurbaniDisclaimer = lazy(() => import('./pages/qurbani/QurbaniDisclaimer'));
const QurbaniLandingPage = lazy(() => import('./pages/qurbani/QurbaniLandingPage'));
const ShortUrlRedirect = lazy(() => import('./pages/ShortUrlRedirect'));

// ============================================
// Protected User Pages
// ============================================
const MyOrderPage = lazy(() => import('./pages/profile/MyOrderPage'));
const MyOrderDetailPage = lazy(() => import('./pages/profile/MyOrderDetailPage'));
const BillingAddress = lazy(() => import('./pages/profile/BillingAddress'));
const DeliveryMethod = lazy(() => import('./pages/profile/DeliveryMethod'));
const PaymentDetails = lazy(() => import('./pages/profile/PaymentDetails'));
const ResetPassword = lazy(() => import('./pages/profile/ResetPassword'));
const Rewards = lazy(() => import('./pages/profile/Rewards'));
const Referrals = lazy(() => import('./pages/profile/Referrals'));
const CheckOutPage = lazy(() => import('./pages/CheckOutPage'));
const OrderSummaryPage = lazy(() => import('./pages/OrderSummaryPage'));
const ZellePaymentPage = lazy(() => import('./pages/ZellePaymentPage'));
const MyProfile = lazy(() => import('./pages/profile/MyProfile'));
const Success = lazy(() => import('./pages/Success'));
const MyVouchers = lazy(() => import('./pages/profile/MyVouchers'));

// ============================================
// Admin Pages
// ============================================
const OrdersList = lazy(() => import('./pages/admin/OrdersList'));
const AdminOrderViewPage = lazy(() => import('./pages/admin/AdminOrderViewPage'));
const DeliveryPartnersListPage = lazy(() => import('./pages/admin/DeliveryPartnersListPage'));
const ProductManagement = lazy(() => import('./pages/admin/ProductManagement'));
const NotificationListPage = lazy(() => import('./pages/admin/NotificationListPage'));
const PrintLabelPage = lazy(() => import('./pages/admin/PrintLabelPage'));
const QurbaniFeedBackPage = lazy(() => import('./pages/admin/QurbaniFeedBackPage'));
const UserManagementListPage = lazy(() => import('./pages/admin/UserManagementListPage'));
const CurOffTimePage = lazy(() => import('./pages/admin/CurOffTimePage'));
const DriversAssignmentPage = lazy(() => import('./pages/admin/DriversAssignmentPage'));
const CronManagementPage = lazy(() => import("./pages/admin/CronManagementPage"));
const ProductDetailsPage = lazy(() => import('./pages/admin/ProductDetailsPage'));
const CategoryListPage = lazy(() => import('./pages/admin/CategoryListPage'));
const PreferenceListPage = lazy(() => import('./pages/admin/PreferenceListPage'));
const BestsellerProductsPage = lazy(() => import('./pages/admin/BestsellerProductsPage'));
const EditNotificationPage = lazy(() => import('./pages/admin/EditNotificationPage'));
const OrderProductSummary = lazy(() => import('./pages/admin/OrderProductSummary'));
const OrderTrackingList = lazy(() => import('./pages/admin/OrderTrackingList'));
const PartnerOrders = lazy(() => import('./pages/admin/PartnerOrders'));
const AdminViewUserDetailsPage = lazy(() => import('./pages/admin/AdminViewUserDetailsPage'));
const AdminSubscriptionsList = lazy(() => import('./pages/admin/AdminSubscriptionsList'));
const AdminRefundPage = lazy(() => import('./pages/admin/AdminRefundPage'));
const AdminViewDriverDetailsPage = lazy(() => import('./pages/admin/AdminViewDriverDetailsPage'));
const AdminSuperSetPage = lazy(() => import('./pages/admin/AdminSuperSetPage'));
const PromoCodeManagement = lazy(() => import('./pages/admin/PromoCodeManagement'));
const AdminAPIPage = lazy(() => import('./pages/admin/AdminAPIPage'));
const ReviewsListPage = lazy(() => import('./pages/admin/ReviewsListPage'));
const RefundListPage = lazy(() => import('./pages/admin/RefundListPage'));
const AdminSystemAlertsPage = lazy(() => import('./pages/admin/AdminSystemAlertsPage'));
const PushNotificationPage = lazy(() => import('./pages/admin/PushNotificationPage'));
const CoreSettingsPage = lazy(() => import('./pages/admin/CoreSettingsPage'));
const SettingsManagementPage = lazy(() => import('./pages/admin/SettingsManagementPage'));
const WishlistListPage = lazy(() => import('./pages/admin/WishlistListPage'));
const AdminReferralsListPage = lazy(() => import('./pages/admin/AdminReferralsListPage'));
const VoucherManagementPage = lazy(() => import('./pages/admin/VoucherManagementPage'));

// ============================================
// Error & Layout Components
// ============================================
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const MainLayout = lazy(() => import('./components/layout/MainLayout'));
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));

// ============================================
// Device Detection Hook
// ============================================
const useDeviceType = () => {
  const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 1024);

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { isDesktop, isMobile: !isDesktop };
};

// Routes that open modals on desktop instead of navigating
const DESKTOP_MODAL_ROUTES = [
  '/signin',
  '/signup',
  '/otp-verify',
  '/phone-verify-forgot-pass',
  '/buy',
];

// ============================================
// Cart Protected Route
// Ensures cart has items before accessing cart-related pages
// ============================================
const CartProtectedRoute = ({ children }) => {
  const { cartData } = useSelector((state) => state.cart);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if cart is empty
    const isCartEmpty =
      !cartData ||
      Object.keys(cartData).length === 0 ||
      !cartData.item_info ||
      Object.keys(cartData.item_info).length === 0;

    // Don't redirect on checkout page - let CheckOutPage handle its own logic during signup
    const isCheckoutPage = location.pathname === '/checkout';

    // Redirect to home if cart is empty (except on checkout page)
    if (isCartEmpty && !isCheckoutPage) {
      navigate('/', { replace: true });
    }
  }, [cartData, navigate, location.pathname]);

  // Check if cart is empty
  const isCartEmpty =
    !cartData ||
    Object.keys(cartData).length === 0 ||
    !cartData.item_info ||
    Object.keys(cartData.item_info).length === 0;

  const isCheckoutPage = location.pathname === '/checkout';

  // Don't render if cart is empty (except on checkout page)
  if (isCartEmpty && !isCheckoutPage) {
    return null;
  }

  return children;
};

// ============================================
// Smart Auth Route
// Redirects auth routes to home on desktop (modals handle auth)
// ============================================
const SmartAuthRoute = ({ children, redirectTo = '/' }) => {
  const { isDesktop } = useDeviceType();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // On desktop, redirect auth routes to home - navbar will open modals
    if (isDesktop && DESKTOP_MODAL_ROUTES.includes(location.pathname)) {
      navigate(redirectTo, { replace: true });
    }
  }, [isDesktop, location.pathname, navigate, redirectTo]);

  // Render component on mobile only
  if (!isDesktop) {
    return children;
  }

  // Don't render on desktop (will be redirected)
  return null;
};

// ============================================
// Mobile Only Route
// Redirects to home on desktop devices
// ============================================
export const MobileOnlyRoute = ({ children }) => {
  const { isDesktop } = useDeviceType();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to home on desktop
    if (isDesktop) {
      navigate('/', { replace: true });
    }
  }, [isDesktop, navigate]);

  // Only render on mobile
  if (isDesktop) {
    return null;
  }

  return children;
};

// ============================================
// Protected Route
// Requires authentication to access
// ============================================
export const ProtectedRoute = ({ children }) => {
  const reduxToken = useSelector(selectToken);
  const location = useLocation();

  // Check localStorage if Redux has no token
  const localToken = localStorage.getItem('token');
  const token = reduxToken || localToken;

  const isAuthenticated = !!token;

  // Redirect to signin if not authenticated
  if (!isAuthenticated) {
    return <Navigate to='/signin' state={{ from: location.pathname }} replace />;
  }

  return children;
};

// ============================================
// Admin Route
// Requires admin authentication to access
// ============================================
export const AdminRoute = ({ children }) => {
  const reduxToken = useSelector(selectToken);
  const location = useLocation();

  // Check localStorage if Redux has no token
  const localToken = localStorage.getItem('token');
  const token = reduxToken || localToken;

  const isAuthenticated = !!token;

  // Redirect to signin if not authenticated
  if (!isAuthenticated) {
    return <Navigate to='/signin' state={{ from: location.pathname }} replace />;
  }

  return children;
};

// ============================================
// Guest Only Route
// Only accessible when not logged in
// ============================================
export const GuestOnlyRoute = ({ children }) => {
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);

  // Redirect to home if already logged in
  if (token && user) {
    return <Navigate to='/' replace />;
  }

  return children;
};

// ============================================
// Routes Configuration
// ============================================
const createRoutes = () => [
  // ========== Authentication Routes ==========
  {
    path: '/signin',
    element: (
      <SmartAuthRoute>
        <GuestOnlyRoute>
          <Signin />
        </GuestOnlyRoute>
      </SmartAuthRoute>
    ),
  },
  {
    path: '/signup',
    element: (
      <SmartAuthRoute>
        <GuestOnlyRoute>
          <SignUp />
        </GuestOnlyRoute>
      </SmartAuthRoute>
    ),
  },
  {
    path: '/otp-verify',
    element: (
      <SmartAuthRoute>
        <GuestOnlyRoute>
          <OtpVerification />
        </GuestOnlyRoute>
      </SmartAuthRoute>
    ),
  },
  {
    path: '/phone-verify-forgot-pass',
    element: (
      <SmartAuthRoute>
        <GuestOnlyRoute>
          <ForgetPassword />
        </GuestOnlyRoute>
      </SmartAuthRoute>
    ),
  },
  {
    path: '/welcome',
    element: <WelcomePage />,
  },
  {
    path: '/reset-password-form',
    element: <UserReSetPassword />,
  },

  // ========== Admin Routes ==========
  {
    path: '/user-details/:userId',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminViewUserDetailsPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/user-details/:userId/driver-view',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminViewDriverDetailsPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/product-management',
    element: (
      <AdminRoute>
        <AdminLayout>
          <ProductDetailsPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/category-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <CategoryListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/best-seller-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <BestsellerProductsPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/preference-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <PreferenceListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/orders-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <OrdersList />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/promo-code-management',
    element: (
      <AdminRoute>
        <AdminLayout>
          <PromoCodeManagement />
        </AdminLayout>
      </AdminRoute>
    ),
  },

  {
    path: '/product-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <ProductManagement />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/notification-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <NotificationListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/print-label/',
    element: (
      <AdminRoute>
        <PrintLabelPage />
      </AdminRoute>
    ),
  },
  {
    path: '/custom-print-label',
    element: (
      <AdminRoute>
        <CustomPrintLabelPage />
      </AdminRoute>
    ),
  },
  {
    path: '/qurbani-feedback',
    element: (
      <AdminRoute>
        <AdminLayout>
          <QurbaniFeedBackPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/user-management',
    element: (
      <AdminRoute>
        <AdminLayout>
          <UserManagementListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/delivery-partners-management',
    element: (
      <AdminRoute>
        <AdminLayout>
          <DeliveryPartnersListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/add-cut-off-time',
    element: (
      <AdminRoute>
        <AdminLayout>
          <CurOffTimePage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/driver-assignment',
    element: (
      <AdminRoute>
        <AdminLayout>
          <DriversAssignmentPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/edit-notification/:notificationId',
    element: (
      <AdminRoute>
        <AdminLayout>
          <EditNotificationPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/view-order-details/:orderId',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminOrderViewPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/order-product-summary',
    element: (
      <AdminRoute>
        <AdminLayout>
          <OrderProductSummary />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/order-tracking-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <OrderTrackingList />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/cron-management',
    element: (
      <AdminRoute>
        <AdminLayout>
          <CronManagementPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/order-tracking',
    element: (
      <AdminLayout>
        <OrderTracking />
      </AdminLayout>
    ),
  },
  {
    path: '/partner-orders',
    element: (
      <AdminRoute>
        <AdminLayout>
          <PartnerOrders />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/order-pending-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <OrderValidationList />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/zelle-payment-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <ZellePaymentList />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/subscription-details/:id',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminSubscriptionDetailsPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/subscription-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminSubscriptionsList />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/admin-refund',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminRefundPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/reviews-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <ReviewsListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/refund-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <RefundListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/superset',

    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminSuperSetPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/api-docs',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminAPIPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/payment-alerts',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminSystemAlertsPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/push-notifications',
    element: (
      <AdminRoute>
        <AdminLayout>
          <PushNotificationPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/wishlist-list',
    element: (
      <AdminRoute>
        <AdminLayout>
          <WishlistListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/admin-referrals',
    element: (
      <AdminRoute>
        <AdminLayout>
          <AdminReferralsListPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/gift-card-management',
    element: (
      <AdminRoute>
        <AdminLayout>
          <VoucherManagementPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  {
    path: '/settings-management',
    element: (
      <AdminRoute>
        <AdminLayout>
          <SettingsManagementPage />
        </AdminLayout>
      </AdminRoute>
    ),
  },
  // ========== Protected User Routes ==========
  {
    path: '/my-account',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <MyProfile />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/orders',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <MyOrderPage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/subscriptions',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <SubscriptionsList />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/subscriptions/:id',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <SubscriptionsDetails />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/orders/:orderId',
    element: (
      <MainLayout>
        <MyOrderDetailPage />
      </MainLayout>
    ),
  },
  {
    path: '/billing-address',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <BillingAddress />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/delivery-method',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <DeliveryMethod />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/payment-details',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <PaymentDetails />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/password-settings',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <ResetPassword />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/rewards',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Rewards />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/referrals',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Referrals />
          </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/my-gift-cards',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <MyVouchers />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/checkout',
    element: (
      <CartProtectedRoute>
        <MainLayout>
          <CheckOutPage />
        </MainLayout>
      </CartProtectedRoute>
    ),
  },
  {
    path: '/order-summary',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <OrderSummaryPage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/zelle/:transactionId',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <ZellePaymentPage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },

  // ========== Public Routes ==========
  {
    path: '/',
    element: (
      <MainLayout>
        <HomePage />
      </MainLayout>
    ),
  },
  {
    path: '/success',
    element: (
      <MainLayout>
        <Success />
      </MainLayout>
    ),
  },
  {
    path: '/cancel',
    element: (
      <MainLayout>
        <Cancel />
      </MainLayout>
    ),
  },
  {
    path: '/faq',
    element: (
      <MainLayout>
        <FaqPage />
      </MainLayout>
    ),
  },
  {
    path: '/qurbani',
    element: (
      <MainLayout>
        <QurbaniLandingPage />
      </MainLayout>
    ),
  },
  {
    path: '/qurbani/order',
    element: (
      <MainLayout>
        <QurbaniPage />
      </MainLayout>
    ),
  },
  {
    path: '/qurbani/checkout',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <QurbaniCheckoutStepper />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/qurbani/order-summary',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <QurbaniOrderSummary />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/qurbani/disclaimer',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <QurbaniDisclaimer />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/qurbani/:farmName',
    element: (
      <MainLayout>
        <QurbaniPage />
      </MainLayout>
    ),
  },
  {
    path: '/:categoryName/:alias-name',
    element: (
      <MainLayout>
        <ProductDetailPage />
      </MainLayout>
    ),
  },
  {
    path: '/:alias_name',
    element: (
      <MainLayout>
        <ProductsByCategoryPage />
      </MainLayout>
    ),
  },
  {
    path: '/about-us',
    element: (
      <MainLayout>
        <AboutUsPage />
      </MainLayout>
    ),
  },
  {
    path: '/packaging',
    element: (
      <MainLayout>
        <Packaging />
      </MainLayout>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <MainLayout>
        <PrivacyPolicyPage />
      </MainLayout>
    ),
  },
  {
    path: '/feedback',
    element: (
      <MainLayout>
        <FeedbackPage />
      </MainLayout>
    ),
  },
  {
    path: '/why-halalfull',
    element: (
      <MainLayout>
        <WhyHalal />
      </MainLayout>
    ),
  },
  {
    path: '/happy-customers',
    element: (
      <MainLayout>
        <HappyCustomersPage />
      </MainLayout>
    ),
  },
  {
    path: '/terms-conditions',
    element: (
      <MainLayout>
        <TermsConditionsPage />
      </MainLayout>
    ),
  },
  {
    path: '/buy',
    element: (
      <SmartAuthRoute>
        <MainLayout>
          <BuyPage />
        </MainLayout>
      </SmartAuthRoute>
    ),
  },
  {
    path: '/other-pages',
    element: (
      <MobileOnlyRoute>
        <MainLayout>
          <OthersPages />
        </MainLayout>
      </MobileOnlyRoute>
    ),
  },
  {
    path: '/how-it-works',
    element: (
      <MainLayout>
        <HowItWorks />
      </MainLayout>
    ),
  },
  {
    path: '/delivery-partners',
    element: (
      <MainLayout>
        <DeliveryPartners />
      </MainLayout>
    ),
  },
  {
    path: '/delivery-partners/:alias',
    element: (
      <MainLayout>
        <DeliveryPartners />
      </MainLayout>
    ),
  },

  // ========== Coming Soon Page ==========
  {
    path: '/coming-soon',
    element: (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-900 mb-4'>Coming Soon</h1>
          <p className='text-gray-600'>This feature will be available soon!</p>
        </div>
      </div>
    ),
  },

  // ========== Short URL Redirect Route ==========
  {
    path: '/:shortCode',
    element: <ShortUrlRedirect />,
  },

  // ========== 404 Page - Must be last ==========
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default createRoutes;
